import { generateRoundRobinSchedule, generateDoubleRoundRobin } from './matchScheduler';

/**
 * Adds minutes to a time string (HH:MM)
 * @param {string} timeStr - "09:00"
 * @param {number} minutes - 20
 * @returns {string} "09:20"
 */
const addMinutes = (timeStr, minutes) => {
    const [h, m] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(h, m, 0, 0);
    date.setMinutes(date.getMinutes() + minutes);
    return date.toTimeString().slice(0, 5);
};

/**
 * Checks if a team has played in the previous slot
 */
const hasPlayedRecently = (teamId, lastPlayedSlotIndex, currentSlotIndex) => {
    if (lastPlayedSlotIndex[teamId] === undefined) return false;
    // If they played in the immediately preceding slot, they "played recently"
    return lastPlayedSlotIndex[teamId] === currentSlotIndex - 1;
};

/**
 * Main Smart Scheduler Function
 * @param {Array} poules - Array of poule objects { id, name }
 * @param {Array} teams - Array of ALL teams (will be filtered by poule)
 * @param {Object} settings - { startTime, matchDuration, breakDuration, fields, type ('single'/'double') }
 */
export const generateSmartSchedule = (poules, teams, settings) => {
    const {
        startTime = '09:00',
        matchDuration = 20,
        breakDuration = 5,
        fields = ['Veld 1'],
        type = 'single'
    } = settings;

    // 1. Generate all RAW matches for all selected poules
    let allMatches = [];

    poules.forEach(poule => {
        // Filter teams for this poule logic
        // Note: The caller should ensure 'teams' contains the teams for these poules
        // We use loose comparison for safety regarding string/int IDs
        let pouleTeams = teams.filter(t => t.poule_id == poule.id);

        // Fallback for demo/testing if poule structure is loose
        if (pouleTeams.length < 2) return;

        // Generate raw matches using the existing verified algorithms
        const rawMatches = type === 'double'
            ? generateDoubleRoundRobin(pouleTeams, poule.id)
            : generateRoundRobinSchedule(pouleTeams, poule.id);

        allMatches = [...allMatches, ...rawMatches];
    });

    if (allMatches.length === 0) {
        throw new Error('Geen wedstrijden om te plannen (te weinig teams?)');
    }

    // 2. Scheduler State
    const scheduledMatches = [];
    let matchQueue = [...allMatches];

    // Sort queue? Maybe randomize to mix poules? 
    // Let's shuffle slightly to ensure fair distribution if multiple poules
    matchQueue.sort(() => Math.random() - 0.5);

    let currentTime = startTime;
    let slotIndex = 0;

    // Track when teams last played to enforce rest
    // Map<TeamID, SlotIndex>
    const teamLastPlayedSlot = {};

    // 3. Process Slots until queue is empty
    while (matchQueue.length > 0) {
        const slotMatches = [];
        const slotTeams = new Set(); // Teams playing in THIS slot

        // Try to fill all available fields for this slot
        for (const field of fields) {
            // Find a suitable match for this field
            let bestMatchIndex = -1;

            // Strategy: Find first match where neither team is playing NOW 
            // AND preferably neither team played in the LAST slot (Rest)

            // Pass 1: Strict Rest (Neither team played in previous slot)
            bestMatchIndex = matchQueue.findIndex(m => {
                const homeBusy = slotTeams.has(m.home_team_id);
                const awayBusy = slotTeams.has(m.away_team_id);
                if (homeBusy || awayBusy) return false;

                const homeTired = hasPlayedRecently(m.home_team_id, teamLastPlayedSlot, slotIndex);
                const awayTired = hasPlayedRecently(m.away_team_id, teamLastPlayedSlot, slotIndex);
                return !homeTired && !awayTired;
            });

            // Pass 2: Relaxed Rest (Allow back-to-back if we must, but never same slot)
            if (bestMatchIndex === -1) {
                bestMatchIndex = matchQueue.findIndex(m => {
                    const homeBusy = slotTeams.has(m.home_team_id);
                    const awayBusy = slotTeams.has(m.away_team_id);
                    return !homeBusy && !awayBusy;
                });
            }

            if (bestMatchIndex !== -1) {
                // Schedule this match
                const match = matchQueue[bestMatchIndex];
                matchQueue.splice(bestMatchIndex, 1); // Remove from queue

                // Assign details
                match.match_time = currentTime + ':00'; // DB format HH:MM:SS
                match.field = field;
                match.referee = null; // Could add referee logic here too later

                // Update tracking
                slotMatches.push(match);
                slotTeams.add(match.home_team_id);
                slotTeams.add(match.away_team_id);

                teamLastPlayedSlot[match.home_team_id] = slotIndex;
                teamLastPlayedSlot[match.away_team_id] = slotIndex;
            }
        }

        // Add confirmed matches to final list
        scheduledMatches.push(...slotMatches);

        // Move to next time slot
        if (matchQueue.length > 0) {
            const totalSlotTime = parseInt(matchDuration) + parseInt(breakDuration);
            currentTime = addMinutes(currentTime, totalSlotTime);
            slotIndex++;
        } else {
            break; // Done!
        }

        // Safety break to prevent infinite loops if something is weird
        if (slotIndex > 1000) {
            console.error("Infinite loop protection triggered in scheduler");
            break;
        }
    }

    // Sort result by time and field for nice list
    scheduledMatches.sort((a, b) => {
        if (a.match_time !== b.match_time) return a.match_time.localeCompare(b.match_time);
        return a.field.localeCompare(b.field);
    });

    return scheduledMatches;
};
